/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import { TableCell, TableRow } from "@material-ui/core";

import { useSelector } from "react-redux";
import { RootState, SelfObject } from "store/types";

import { Text, UserCell } from "UpduoLibrary";
import { skaleBlack, upduoLightestGray } from "config/UpduoColors";
import { title, formattedDatetimeToDateString } from "config/Utilities";
import PermissionsDropDown from "../PermissionsDropDown";

const MEMBER_PERMISSIONS = [
  "participant",
  "manager",
  "content editor",
  "admin",
];

interface MemberPermissions {
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
}

function getMemberPermissions(member: MemberPermissions): string {
  if (member.isAdmin) {
    return "admin";
  } else if (member.isStaff) {
    return "content editor";
  } else if (member.isManager) {
    return "manager";
  } else {
    return "participant";
  }
}

interface MemberRowProps {
  rowData: any;
  userIndex: number;
  changePermissions: any;
}

const memberCell = (item: string, itemIndex: number) => (
  <TableCell
    key={itemIndex}
    align={"left"}
    style={{ borderBottomColor: upduoLightestGray }}
  >
    <Text style={{ color: skaleBlack }}>{item}</Text>
  </TableCell>
);

export default function MemberRow({
  rowData,
  userIndex,
  changePermissions,
}: MemberRowProps) {
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );

  const permissions = getMemberPermissions(rowData);
  const additionalColumnTitleArray = [
    rowData.title ? rowData.title : "-",
    rowData.internalId ? rowData.internalId : "-",
    rowData.primaryEmail,
    rowData.region ? rowData.region.name : "-",
    rowData.district ? rowData.district.name : "-",
    rowData.store ? rowData.store.name : "-",
    rowData.hireDate ? rowData.hireDate : "-",
    rowData.lastActive
      ? formattedDatetimeToDateString(rowData.lastActive)
      : "-",
    formattedDatetimeToDateString(rowData.created),
  ];

  // If the logged-in user is an admin they can edit roles/permissions
  const isAdmin = selfObject.isAdmin;
  // If the logged-in user is staff they can edit coach/trainer status)=

  return (
    <TableRow hover={true} key={0}>
      <TableCell
        key={0}
        align={"center"}
        style={{ borderBottomColor: upduoLightestGray }}
      >
        <Text style={{ color: skaleBlack }}>{userIndex + 1}</Text>
      </TableCell>
      <TableCell key={1} style={{ borderBottomColor: upduoLightestGray }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: 200,
          }}
        >
          <UserCell user={rowData} isGraphql={true} />
        </View>
      </TableCell>
      {additionalColumnTitleArray.map(memberCell)}
      {selfObject.isStaff ? (
        <>
          <TableCell style={{ borderBottomColor: upduoLightestGray }}>
            <PermissionsDropDown
              onChange={(value: string) =>
                changePermissions({ isCoach: value === "yes" })
              }
              selectedText={rowData.isCoach ? "yes" : "no"}
              optionsArray={["yes", "no"]}
            />
          </TableCell>
          <TableCell style={{ borderBottomColor: upduoLightestGray }}>
            <PermissionsDropDown
              onChange={(value: string) =>
                changePermissions({ isTrainer: value === "yes" })
              }
              selectedText={rowData.isTrainer ? "yes" : "no"}
              optionsArray={["yes", "no"]}
            />
          </TableCell>
        </>
      ) : (
        <>
          {memberCell(rowData.isCoach ? "Yes" : "No", 100)}
          {memberCell(rowData.isTrainer ? "Yes" : "No", 101)}
        </>
      )}
      {isAdmin ? (
        <TableCell style={{ borderBottomColor: upduoLightestGray }}>
          {selfObject.userId === rowData.id ? (
            <Text style={{ color: skaleBlack }}>{title(permissions)}</Text>
          ) : (
            <PermissionsDropDown
              onChange={(value: string) => {
                const flags = {
                  isAdmin: false,
                  isManager: false,
                  isStaff: false,
                };
                switch (value) {
                  case "admin":
                    flags.isAdmin = true;
                    break;
                  case "manager":
                    flags.isManager = true;
                    break;
                  case "content editor":
                    flags.isStaff = true;
                    break;
                }
                changePermissions(flags);
              }}
              selectedText={permissions}
              optionsArray={MEMBER_PERMISSIONS}
            />
          )}
        </TableCell>
      ) : (
        memberCell(title(permissions), 102)
      )}
    </TableRow>
  );
}
